import { MoonIcon } from "@phosphor-icons/react/dist/csr/Moon";
import { SunIcon } from "@phosphor-icons/react/dist/csr/Sun";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandInput,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { resolvedTheme, useTheme } from "@volca/ui/theme";
import type { NavItem } from "@/app-shell/use-nav-items";
import type { SettingsNav } from "@/app-shell/use-settings-nav";
import { CommandMenuPage } from "./command-menu-page";
import { CommandMenuRoot } from "./command-menu-root";
import {
  COMMAND_ROOT,
  commandPageLabel,
  commandPagePlaceholder,
  type CommandPage,
} from "./pages";

export const CommandMenuDialog = ({
  open,
  query,
  stack,
  items,
  settings,
  showHelp,
  onImpersonate,
  onQueryChange,
  onOpenChange,
  onPush,
  onPop,
  onJump,
}: {
  open: boolean;
  query: string;
  stack: CommandPage[];
  items: NavItem[];
  settings: SettingsNav | null;
  showHelp: boolean;
  onImpersonate?: () => void;
  onQueryChange: (query: string) => void;
  onOpenChange: (open: boolean) => void;
  onPush: (page: CommandPage) => void;
  onPop: () => void;
  onJump: (index: number) => void;
}) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const dark = resolvedTheme(theme) === "dark";
  const ThemeIcon = dark ? SunIcon : MoonIcon;
  const page = stack[stack.length - 1] ?? COMMAND_ROOT;

  const go = (to: string, after?: () => void) => {
    after?.();
    void navigate(to);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-1/3 translate-y-0 overflow-hidden p-0 sm:max-w-lg"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>Search pages, actions and help.</DialogDescription>
        </DialogHeader>
        <Command
          onKeyDown={(event: ReactKeyboardEvent) => {
            if (event.key !== "Backspace" || query !== "" || stack.length < 2) {
              return;
            }
            event.preventDefault();
            onPop();
          }}
        >
          {stack.length > 1 && (
            <Stack direction="row" align="center" gap={1} className="px-2 pt-2">
              {stack.slice(1).map((crumb, index) => {
                const label = commandPageLabel(crumb);
                const stackIndex = index + 1;
                const current = stackIndex === stack.length - 1;
                if (!label) return null;
                return current ? (
                  <span
                    key={`${crumb.kind}-${stackIndex}`}
                    className="rounded-md bg-neutral px-1.5 py-0.5 text-xs text-ink"
                  >
                    {label}
                  </span>
                ) : (
                  <button
                    key={`${crumb.kind}-${stackIndex}`}
                    type="button"
                    className="rounded-md bg-neutral px-1.5 py-0.5 text-xs text-subtle hover:bg-neutral-hovered hover:text-ink"
                    onClick={() => onJump(stackIndex)}
                  >
                    {label}
                  </button>
                );
              })}
            </Stack>
          )}
          <CommandInput
            placeholder={commandPagePlaceholder(page)}
            value={query}
            onValueChange={onQueryChange}
          />
          <CommandList>
            {page.kind === "root" ? (
              <CommandMenuRoot
                items={items}
                settings={settings}
                open={open}
                query={query}
                showHelp={showHelp}
                themeLabel={dark ? "Use light mode" : "Use dark mode"}
                ThemeIcon={ThemeIcon}
                onPush={onPush}
                onGo={go}
                onImpersonate={onImpersonate}
                onClose={() => onOpenChange(false)}
                onTheme={() => {
                  setTheme(dark ? "light" : "dark");
                  onOpenChange(false);
                }}
              />
            ) : (
              open && (
                <CommandMenuPage
                  page={page}
                  query={query}
                  onPush={onPush}
                  onNavigate={go}
                />
              )
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
